import type { Brand } from '@zg-rentals/multibrand-support';
import { List, Toast, useToast } from '@zillow/constellation-10';
import {
  AutoResponseMessage,
  HighPrioritySystemMessage,
  LowPrioritySystemMessage,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import { logAppError } from '../../utils/errorUtils';
import ChatDisabledMessage from './components/ChatDisabledMessage';
import ListingMessageItem from './components/ListingMessageItem';
import PhoneLead from './components/PhoneLead';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import {
  SatelliteAiAutoResponseType,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import type { AttachmentsForMessage } from '../MessageAttachments';
import type { DecoratedMessage } from '../MessageList';
import { ListingAvailability, MessageTypes } from '../../types/conversationMessages';
import ConfigConstants from '../../constants/ConfigConstants';
import { useSatelliteWebRenterConversationHandler_addMessage } from '@zg-rentals/ts-satellite-client-react-query';

const TOUR_MESSAGE_TYPES = new Set<string>(Object.values(MESSAGE_TYPES.SYSTEM_MESSAGE.TOURS));

const LISTING_UNAVAILABLE_OFFMARKET_MESSAGE = 'Sorry but this listing has been taken off market.';
const LISTING_UNAVAILABLE_UNKNOWN_MESSAGE =
  'We are unable to show this rental listing at this time. Please try again soon.';

type Props = {
  brand: Brand;
  conversationId: string;
  isLastMessageInList: boolean;
  listingAvailability: ListingAvailability;
  message: DecoratedMessage;
  messageKey: string;
  selectedFolder: SatelliteConversationFolderTypeV2;
  sendStatus?: MessageStatusVariant;
  shouldFetchOlderConversations: boolean;
  updateTemporaryMessage(messageKey: string, updates: Partial<DecoratedMessage>): void;
};

const getAttachmentZDocIdsForAddMessageV2Mutation = (attachments: AttachmentsForMessage): Array<string> => {
  return attachments.map(({ zDocId }) => zDocId || '');
};

const MessageItem = ({
  brand,
  conversationId,
  isLastMessageInList,
  listingAvailability,
  message: messageObject,
  messageKey,
  selectedFolder,
  sendStatus = MESSAGE_STATUS.SENT,
  shouldFetchOlderConversations,
  updateTemporaryMessage,
}: Props) => {
  const {
    aiAutoResponseType,
    attachments,
    isMessageOwner,
    message: messageContent = '',
    messageDateMs = '',
    messageLink,
    messageId = '',
    messageType,
    senderName = '',
    title = '',
    uiTreatmentType,
  } = messageObject;

  const { mutateAsync: addMessage } = useSatelliteWebRenterConversationHandler_addMessage();
  const queryClient = useQueryClient();
  const ctaText = messageLink?.text ?? '';
  const systemMessageUrl = messageLink?.url ?? '';
  const isHighPrioritySystemMessage =
    uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY || uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY_WITH_LOGO;
  const shouldDisplaySentStatus = !!messageDateMs && isLastMessageInList && !!isMessageOwner;
  const isTourRelatedMessage = TOUR_MESSAGE_TYPES.has(messageType);
  const { enqueueToast } = useToast();

  // Show toast instead of navigating when a tour CTA targets an offmarket listing
  const shouldBlockTourCtaNavigation = isTourRelatedMessage && listingAvailability !== ListingAvailability.Available;

  const handleBlockedTourCtaClick = () => {
    const message =
      listingAvailability === ListingAvailability.Offmarket
        ? LISTING_UNAVAILABLE_OFFMARKET_MESSAGE
        : LISTING_UNAVAILABLE_UNKNOWN_MESSAGE;
    enqueueToast(<Toast tone="info">{message}</Toast>);
  };

  const renderMessage = () => {
    if (aiAutoResponseType === SatelliteAiAutoResponseType.ELISE_AI && messageContent) {
      return (
        <AutoResponseMessage
          enableClickableLinks
          message={messageContent}
          messageDateMs={messageDateMs}
          metaLabelType="eliseAI"
        />
      );
    }

    if (aiAutoResponseType === SatelliteAiAutoResponseType.VAL_AI && messageContent) {
      return (
        <AutoResponseMessage
          enableClickableLinks
          message={messageContent}
          messageDateMs={messageDateMs}
          metaLabelType="valAI"
        />
      );
    }

    switch (messageType) {
      case MessageTypes.ListingMessage:
        return (
          <ListingMessageItem
            attachments={attachments}
            conversationId={conversationId}
            handleResendMessage={handleResendMessage}
            isMessageOwner={!!isMessageOwner}
            messageContent={messageContent!}
            messageDateMs={messageDateMs}
            messageId={messageId!}
            senderName={senderName!}
            sendStatus={sendStatus}
            shouldDisplaySentStatus={shouldDisplaySentStatus}
          />
        );
      case MessageTypes.PhoneLead:
        return (
          <PhoneLead isMessageOwner={!!isMessageOwner} messageContent={messageContent!} messageDateMs={messageDateMs} />
        );

      case MessageTypes.NotAcceptingReplies:
        return <ChatDisabledMessage brand={brand} message={messageContent || ''} />;
      default:
        return (
          <>
            {isHighPrioritySystemMessage ? (
              <HighPrioritySystemMessage
                ctaText={ctaText}
                onCtaClick={shouldBlockTourCtaNavigation ? handleBlockedTourCtaClick : undefined}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                messageType={messageType}
                persona="renter"
                title={title ?? ''}
                uiTreatmentType={uiTreatmentType!}
                url={shouldBlockTourCtaNavigation ? undefined : systemMessageUrl}
              />
            ) : (
              <LowPrioritySystemMessage
                ctaText={ctaText}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                url={systemMessageUrl}
              />
            )}
          </>
        );
    }
  };

  const handleResendMessage = async () => {
    try {
      updateTemporaryMessage(messageKey, { sendStatus: MESSAGE_STATUS.SENDING });
      const zDocIds = attachments.length > 0 ? getAttachmentZDocIdsForAddMessageV2Mutation(attachments) : [];

      const result = await addMessage({
        postData: {
          conversationId,
          message: messageContent!,
          uploadedZdocIds: zDocIds,
        },
      });

      if (result) {
        const { messageId, messageDateMs } = result;

        //update temporary msgs array if resend successful
        updateTemporaryMessage(messageKey, {
          messageId: messageId,
          sendStatus: MESSAGE_STATUS.SENT,
          messageDateMs: String(messageDateMs),
        });

        // Invalidate only conversation list; getMessages will refetch once when list refetch completes (ConversationList effect).
        queryClient.invalidateQueries({
          queryKey: [
            SatelliteWebRenterConversationHandler_getLatestConversations_Path,
            {
              folder: selectedFolder,
              daysBack: shouldFetchOlderConversations ? ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS : undefined,
            },
          ],
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      logAppError({
        context: { errorLocation: 'component.MessageItem.handleResendMessage' },
        message: error.message,
        error,
      });

      updateTemporaryMessage(messageKey, { sendStatus: MESSAGE_STATUS.FAILED });
    }
  };

  return (
    <List.Item
      aria-roledescription="message"
      aria-live={isLastMessageInList ? 'polite' : 'off'}
      key={messageKey}
      css={{
        listStyle: 'none',
        marginLeft: isMessageOwner ? 'auto' : 0,
        marginBlock: 'default',
        paddingBlock: 'none',
      }}
    >
      {renderMessage()}
    </List.Item>
  );
};

export default MessageItem;
